import React, { useState } from 'react';
import { Tabs } from 'components/Tabs';
import { PageHeader } from 'components/PageHeader';
import { Heading1 } from 'components/Heading1';
import { PageWrapper } from 'components/PageWrapper';
import { BreadcrumbContainer } from 'components/Breadcrumb';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router-dom';
import { SplashScreen } from 'components/SplashScreen';
import { AccountsService } from 'modules/escrow/services';
import { EscrowRoutes } from 'modules/escrow/constants/routes';
import EscrowDetailsRelatedParts from './EscrowDetailsRelatedParts';
import EscrowDetailsSigners from './EscrowDetailsSigners';
import EscrowDetailsConfiguration from './EscrowDetailsConfiguration';
import EscrowAccountInfoHeader from '../components/EscrowAccountInfoHeader/EscrowAccountInfoHeader';
import { Divider, notification } from 'antd';
import { PageActions } from 'components/PageActions';
import { ButtonSecondary } from './styles';
import { BlockIcon } from 'components/Icons/Block';
import { CancelIcon } from 'components/Icons/Cancel';
import { TextM } from 'components/Text';
import {
	AccountStatus,
	EAccountStatusUpdate,
	IIUpdateAccountStatus,
} from 'types/Account';
import { UnblockIcon } from 'components/Icons/Unblock';
import { InformationModal } from 'components/InformationModal';
import { CheckmarkIcon } from 'components/Icons/Checkmark';
import { useAccountActions } from 'modules/escrow/hooks';
import { queryClient } from 'network/query';
import { useCurrentProfile } from 'modules/core/context/ProfileContext';
import { AccessType } from 'types/Access';
import { FileWarningIcon } from 'components/Icons/FileWarning';
import { ThumbUpIcon } from 'components/Icons/ThumbUp';
import { ThumbDownIcon } from 'components/Icons/ThumbDown';
import { TEventConfirmationModal } from './types';
import EventConfirmationModal from './EventConfirmationModal/EventConfirmationModal';
import { ApiError } from 'types/ApiError';
import {
	getAccountRouteTypeIcon,
	getAccountRouteTypeName,
	useIsEscrowAccountRoute,
} from 'modules/escrow/utils/adminRoute';
import theme from 'theme/theme';
import EscrowPJDetailsInformation from './EscrowDetailsInformation';
import EscrowDetailsAccountDestination from '../components/EscrowDetailsAccountDestination';

const EscrowDetailsPage = () => {
	const { id } = useParams();
	const { type } = useCurrentProfile();
	const [api, contextHolder] = notification.useNotification();
	const isEscrowRoute = useIsEscrowAccountRoute();

	const [isOpenSuccessModal, setIsOpenSuccessModal] = useState(false);
	const [isOpenConfirmationModal, setIsOpenConfirmationModal] =
		useState(false);
	const [eventInfoModal, setEventInfoModal] =
		useState<TEventConfirmationModal>();
	const [successMessage, setSuccessMessage] = useState({
		label: '',
		title: '',
	});

	const { data, isLoading } = useQuery({
		queryKey: ['accountDetails', id],
		queryFn: () => AccountsService.getAccount(id!),
		refetchOnWindowFocus: false,
		enabled: typeof id === 'string',
	});

	const isEditingAccount =
		data?.status === AccountStatus.AWAITING_BACKOFFICE_ANALYSIS;
	const titleBreadcrumb = isEditingAccount
		? 'Análise de abertura de conta'
		: 'Detalhes da conta';
	const baseTitleBreadCrumb = (
		<BreadcrumbContainer>
			{getAccountRouteTypeIcon()}
			<span>{getAccountRouteTypeName()}</span>
		</BreadcrumbContainer>
	);
	const redirectAccount = isEscrowRoute
		? EscrowRoutes.ESCROW_BASE
		: EscrowRoutes.ACCOUNT_BASE;
	const breadcrumb = [
		{
			href: redirectAccount,
			title: baseTitleBreadCrumb,
		},
		{
			title: titleBreadcrumb,
		},
	];

	const { loading, block, cancel, unblock } = useAccountActions({
		onError: e => {
			api.error({
				description: e.data.message,
				message: 'Ocorreu um problema.',
			});
		},
		onBlockSuccess: () => {
			queryClient.refetchQueries({
				queryKey: ['accountDetails', id],
			});
			setSuccessMessage({
				label: 'A conta foi bloqueada com sucesso!',
				title: 'Conta bloqueada',
			});
			setIsOpenSuccessModal(true);
		},
		onUnblockSuccess: () => {
			queryClient.refetchQueries({
				queryKey: ['accountDetails', id],
			});
			setSuccessMessage({
				label: 'A conta foi desbloqueada com sucesso!',
				title: 'Conta desbloqueada',
			});
			setIsOpenSuccessModal(true);
		},
		onCancelSuccess: () => {
			queryClient.refetchQueries({
				queryKey: ['accountDetails', id],
			});
			setSuccessMessage({
				label: 'A conta foi encerrada com sucesso!',
				title: 'Conta encerrada',
			});
			setIsOpenSuccessModal(true);
		},
	});

	const { mutate: updateStatus } = useMutation<
		void,
		ApiError,
		IIUpdateAccountStatus
	>({
		mutationFn: ({ id, transition_event, reason }) => {
			return AccountsService.updateAccountStatus(id, {
				transition_event: transition_event,
				reason: reason,
			});
		},
		onSuccess: (_, data) => {
			if (
				data.transition_event ===
				EAccountStatusUpdate.BACKOFFICE_ANALYSIS_REQUEST_DOCUMENTS
			) {
				setSuccessMessage({
					label: 'A conta foi enviada para revisão de documentação.',
					title: 'Pendência sinalizada com sucesso!',
				});
			}
			if (
				data.transition_event ===
				EAccountStatusUpdate.BACKOFFICE_ANALYSIS_REFUSE
			) {
				setSuccessMessage({
					label: 'A conta foi recusada em análise com sucesso.',
					title: 'Conta recusada com sucesso!',
				});
			}

			if (
				data.transition_event ===
				EAccountStatusUpdate.BACKOFFICE_ANALYSIS_APPROVE
			) {
				setSuccessMessage({
					label: 'A conta foi aprovada na análise e está sendo criada.',
					title: 'Conta aprovada com sucesso!',
				});
			}

			if (
				data.transition_event ===
				EAccountStatusUpdate.BACKOFFICE_ANALYSIS
			) {
				setSuccessMessage({
					label: 'A conta foi enviada para análise.',
					title: 'Conta editada com sucesso!',
				});
			}

			setIsOpenSuccessModal(true);
			queryClient.refetchQueries({
				queryKey: ['accountDetails', id],
			});
		},
		onError: e => {
			api.error({
				description: e.data.message,
				message: 'Ocorreu um problema.',
			});
		},
	});

	const title = isEditingAccount
		? `Análise da conta ${data?.account ?? ''}`
		: `Detalhes da conta ${data?.account ?? ''}`;

	if (isLoading) {
		return <SplashScreen />;
	}

	if (!data) {
		return <Navigate to={EscrowRoutes.ESCROW_BASE} />;
	}

	const tabOptions = [
		{
			title: 'Dados do titular',
			content: (
				<EscrowPJDetailsInformation account={data} hideActions={true} />
			),
			path: 'info',
			breadcrumb,
		},
		{
			title: 'Partes relacionadas',
			content: <EscrowDetailsRelatedParts account={data} />,
			path: 'related-parts',
			breadcrumb,
		},
		{
			title: 'Assinantes',
			content: <EscrowDetailsSigners account={data} hideActions={true} />,
			path: 'signers',
			breadcrumb,
		},
		{
			title: 'Configurações da conta',
			content: <EscrowDetailsConfiguration account={data} />,
			path: 'configs',
			breadcrumb,
		},
		{
			title: 'Contas beneficiárias',
			content: <EscrowDetailsAccountDestination account={data} />,
			path: 'beneficiary-accounts',
			breadcrumb,
		},
	];

	const updateStatusHandler = (
		status: EAccountStatusUpdate,
		reason?: string,
	) => {
		if (
			status ===
			EAccountStatusUpdate.BACKOFFICE_ANALYSIS_REQUEST_DOCUMENTS
		) {
			updateStatus({
				id: data.id,
				transition_event:
					EAccountStatusUpdate.BACKOFFICE_ANALYSIS_REQUEST_DOCUMENTS,
				reason: reason || '',
			});
		}

		if (status === EAccountStatusUpdate.BACKOFFICE_ANALYSIS_REFUSE) {
			updateStatus({
				id: data.id,
				transition_event:
					EAccountStatusUpdate.BACKOFFICE_ANALYSIS_REFUSE,
				reason: reason || '',
			});
		}

		if (status === EAccountStatusUpdate.BACKOFFICE_ANALYSIS_APPROVE) {
			updateStatus({
				id: data.id,
				transition_event:
					EAccountStatusUpdate.BACKOFFICE_ANALYSIS_APPROVE,
				reason: reason || '',
			});
		}

		if (status === EAccountStatusUpdate.BACKOFFICE_ANALYSIS) {
			updateStatus({
				id: data.id,
				transition_event: EAccountStatusUpdate.BACKOFFICE_ANALYSIS,
				reason: reason || '',
			});
		}
	};

	return (
		<>
			{contextHolder}
			<PageHeader isFluid={true}>
				<Heading1>{title}</Heading1>
				{type === AccessType.FINANCIAL_INSTITUTION && (
					<PageActions>
						{data.status === AccountStatus.ACTIVE && (
							<>
								<ButtonSecondary
									type="default"
									onClick={() => {
										setEventInfoModal({
											buttonText: 'Encerrar conta',
											modalTitle: 'Encerrar conta',

											isDanger: true,
											infoPrimary:
												'Você tem certeza que deseja encerrar a conta?',
											infoSecondary:
												'Essa ação não poderá ser desfeita.',
											onClick: () => {
												cancel(data);
												setIsOpenConfirmationModal(
													false,
												);
											},
										});
										setIsOpenConfirmationModal(true);
									}}
								>
									Encerrar conta
									<CancelIcon />
								</ButtonSecondary>
								<ButtonSecondary
									type="default"
									onClick={() => {
										setEventInfoModal({
											buttonText: 'Bloquear conta',
											modalTitle: 'Bloquear conta',
											infoPrimary:
												'Você tem certeza que deseja bloquear a conta?',
											onClick: () => {
												block(data);
												setIsOpenConfirmationModal(
													false,
												);
											},
										});
										setIsOpenConfirmationModal(true);
									}}
								>
									Bloquear conta
									<BlockIcon />
								</ButtonSecondary>
							</>
						)}
						{data.status === AccountStatus.BLOCKED && (
							<ButtonSecondary
								type="default"
								onClick={() => {
									setEventInfoModal({
										buttonText: 'Desbloquear conta',
										modalTitle: 'Desbloquear conta',
										infoPrimary:
											'Você tem certeza que deseja desbloquear a conta?',
										onClick: () => {
											unblock(data);
											setIsOpenConfirmationModal(false);
										},
									});
									setIsOpenConfirmationModal(true);
								}}
							>
								Desbloquear conta
								<UnblockIcon />
							</ButtonSecondary>
						)}
					</PageActions>
				)}
				{type === AccessType.FINANCIAL_INSTITUTION &&
					isEditingAccount && (
						<PageActions>
							<ButtonSecondary
								type="default"
								onClick={() => {
									setEventInfoModal({
										isDanger: true,
										disableIfEmptyReason: true,
										buttonText:
											'Sinalizar pendência de documentação',
										modalTitle:
											'Sinalizar pendência de documentação',
										infoPrimary:
											'Sinalizar que a abertura da conta possui documentos pendentes?',
										inputTextTitle: 'Documentos pendentes',
										onClick: reason => {
											updateStatusHandler(
												EAccountStatusUpdate.BACKOFFICE_ANALYSIS_REQUEST_DOCUMENTS,
												reason,
											);
											setIsOpenConfirmationModal(false);
										},
									});
									setIsOpenConfirmationModal(true);
								}}
							>
								Documentos pendentes
								<FileWarningIcon size={19} />
							</ButtonSecondary>
							<ButtonSecondary
								type="default"
								onClick={() => {
									setEventInfoModal({
										buttonText: 'Rejeitar solicitação',
										modalTitle: 'Rejeitar solicitação',
										infoPrimary: `Você tem certeza que deseja rejeitar a abertura da conta de ${data.person.name}?`,
										inputTextTitle: 'Motivo da rejeição',
										isDanger: true,
										disableIfEmptyReason: true,
										onClick: reason => {
											updateStatusHandler(
												EAccountStatusUpdate.BACKOFFICE_ANALYSIS_REFUSE,
												reason,
											);
											setIsOpenConfirmationModal(false);
										},
									});
									setIsOpenConfirmationModal(true);
								}}
							>
								Recusar
								<ThumbDownIcon />
							</ButtonSecondary>
							{data.status ===
								AccountStatus.AWAITING_BACKOFFICE_ANALYSIS && (
								<ButtonSecondary
									type="primary"
									onClick={() => {
										setEventInfoModal({
											buttonText: 'Aprovar solicitação',
											modalTitle: 'Aprovar solicitação',
											infoPrimary: `Você tem certeza que deseja aprovar a abertura da conta de ${data.person.name}?`,
											inputTextTitle:
												'Comentário (optional)',
											onClick: reason => {
												updateStatusHandler(
													EAccountStatusUpdate.BACKOFFICE_ANALYSIS_APPROVE,
													reason,
												);
												setIsOpenConfirmationModal(
													false,
												);
											},
										});
										setIsOpenConfirmationModal(true);
									}}
								>
									<TextM color={theme.white}>Aprovar</TextM>
									<ThumbUpIcon color={theme.white} />
								</ButtonSecondary>
							)}
						</PageActions>
					)}
				{data.status === AccountStatus.AWAITING_DOCUMENTS_UPDATE && (
					<PageActions>
						<ButtonSecondary
							type="default"
							onClick={() => {
								setEventInfoModal({
									buttonText: 'Enviar para análise',
									modalTitle: 'Enviar para análise',
									isDanger: false,
									infoPrimary:
										'Deseja enviar a conta para análise?',
									onClick: () => {
										updateStatusHandler(
											EAccountStatusUpdate.BACKOFFICE_ANALYSIS,
										);
										setIsOpenConfirmationModal(false);
									},
								});
								setIsOpenConfirmationModal(true);
							}}
						>
							Enviar para análise
							<FileWarningIcon size={19} />
						</ButtonSecondary>
					</PageActions>
				)}
			</PageHeader>
			<EscrowAccountInfoHeader account={data} />
			<Divider style={{ marginTop: 16 }} />
			<PageWrapper isFluid={true}>
				<Tabs prePath={`/${id}/details`} elements={tabOptions} />

				<InformationModal
					buttonText="Ok, obrigado"
					isOpen={isOpenSuccessModal}
					message={successMessage.label}
					title={successMessage.title}
					icon={<CheckmarkIcon />}
					onClose={() => setIsOpenSuccessModal(false)}
				/>

				{isOpenConfirmationModal && eventInfoModal && (
					<EventConfirmationModal
						closeModal={() => setIsOpenConfirmationModal(false)}
						info={eventInfoModal}
						hasInputText={!!eventInfoModal?.inputTextTitle}
						isLoading={loading}
						confirmHandler={reason =>
							eventInfoModal?.onClick(reason || '')
						}
					/>
				)}
			</PageWrapper>
		</>
	);
};

export default EscrowDetailsPage;
